import { useTranslation } from "react-i18next";

import { POUS } from "../framework/data";
import { useFramework, frameworkLabel } from "../framework";
import type { ReportAggregates, WhanauState } from "./reportTypes";
import { WHANAU_STATE_ORDER } from "./whanauState";
import { pct } from "./format";

/**
 * "Download Word" for the reporting dashboard (Sue's wishlist #2).
 *
 * Builds a clean, editable Word document from the de-identified aggregates in
 * view and downloads it — no backend, no new dependency. It emits a Word-
 * compatible HTML document (application/msword), which Word opens as native
 * tables Sue can drop straight into a report. A fully designed .docx/PDF remains
 * the richer future path (the premium plan), but this covers "downloaded clearly
 * into Word" today.
 */
type Props = { agg: ReportAggregates };

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function WordExportButton({ agg }: Props) {
  const { t, i18n } = useTranslation();
  const fw = useFramework();

  const stateLabel = (s: WhanauState) => t(`reports.whanauState.label.${s}`);

  const buildHtml = (): string => {
    const ws = agg.whanauState;
    const generated = new Date().toLocaleString("en-NZ");

    // Whānau-state entry vs exit.
    const wsTable = `
      <h2>${esc(t("reports.whanauState.entryExitTitle"))}</h2>
      <table>
        <tr><th>State</th>
            <th>${esc(t("reports.atEntry"))} (n)</th><th>${esc(t("reports.atEntry"))} %</th>
            <th>${esc(t("reports.atExit"))} (n)</th><th>${esc(t("reports.atExit"))} %</th></tr>
        ${WHANAU_STATE_ORDER.map(
          (s) =>
            `<tr><td>${esc(stateLabel(s))}</td>` +
            `<td>${String(ws.entry[s])}</td><td>${pct(ws.entry.total > 0 ? ws.entry[s] / ws.entry.total : 0)}</td>` +
            `<td>${String(ws.exit[s])}</td><td>${pct(ws.exit.total > 0 ? ws.exit[s] / ws.exit.total : 0)}</td></tr>`,
        ).join("")}
      </table>`;

    // Entry→exit transition.
    const trTable = `
      <h2>${esc(t("reports.whanauState.transitionTitle"))}</h2>
      <table>
        <tr><th>${esc(t("reports.whanauState.entryToExit"))}</th>${WHANAU_STATE_ORDER.map(
          (s) => `<th>${esc(stateLabel(s))}</th>`,
        ).join("")}</tr>
        ${WHANAU_STATE_ORDER.map(
          (from) =>
            `<tr><th>${esc(stateLabel(from))}</th>` +
            WHANAU_STATE_ORDER.map(
              (to) => `<td>${String(ws.transition[from][to])}</td>`,
            ).join("") +
            `</tr>`,
        ).join("")}
      </table>`;

    // Population entry → exit.
    const pop = agg.population;
    const popTable = `
      <h2>Population — entry &#8594; exit</h2>
      <table>
        <tr><th>State</th><th>At entry %</th><th>At exit %</th></tr>
        <tr><td>Risk</td><td>${pct(pop.entryProportion.risk)}</td><td>${pct(pop.exitProportion.risk)}</td></tr>
        <tr><td>Change</td><td>${pct(pop.entryProportion.change)}</td><td>${pct(pop.exitProportion.change)}</td></tr>
        <tr><td>Protective</td><td>${pct(pop.entryProportion.protective)}</td><td>${pct(pop.exitProportion.protective)}</td></tr>
      </table>
      <p>${pop.analysis.total > 0 ? `Improved ${pct(pop.analysis.improved / pop.analysis.total)} &#183; regressed ${pct(pop.analysis.regressed / pop.analysis.total)} (n=${String(pop.analysis.total)})` : "No entry-and-exit data yet."}</p>`;

    // Te Pou summary.
    const pouTable = `
      <h2>Te Pou Mataaho</h2>
      <table>
        <tr><th>Pou</th><th>Improved</th><th>Regressed</th><th>n</th></tr>
        ${POUS.map((pou) => {
          const p = agg.byPou.find((x) => x.pouId === pou.id);
          const tot = p?.analysis.total ?? 0;
          return `<tr><td>${esc(pou.nameMi)}</td><td>${tot > 0 ? pct((p?.analysis.improved ?? 0) / tot) : "&#8211;"}</td><td>${tot > 0 ? pct((p?.analysis.regressed ?? 0) / tot) : "&#8211;"}</td><td>${String(tot)}</td></tr>`;
        }).join("")}
      </table>`;

    // Theory of Change outcomes by domain.
    const tocSection = fw.domains
      .map((domain) => {
        const subs = fw.subdomainsForDomain(domain.id);
        if (subs.length === 0) return "";
        const rows = subs
          .map((s) => {
            const a = agg.bySubdomain.find((x) => x.subdomainId === s.id);
            const tot = a?.analysis.total ?? 0;
            const improved =
              tot > 0 ? pct((a?.analysis.improved ?? 0) / tot) : "&#8211;";
            return `<tr><td>${esc(s.toCOutcome || "—")}</td><td>${esc(frameworkLabel(s, "subdomain", i18n.language, t))}</td><td>${improved}</td></tr>`;
          })
          .join("");
        return `<h3>${esc(frameworkLabel(domain, "domain", i18n.language, t))}</h3>
          <table><tr><th>Outcome</th><th>Subdomain</th><th>Improved entry&#8594;exit</th></tr>${rows}</table>`;
      })
      .join("");

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body { font-family: Calibri, Arial, sans-serif; color: #1f2733; }
      h1 { color: #1d3557; font-size: 20pt; }
      h2 { color: #1d3557; font-size: 14pt; margin-top: 18pt; }
      h3 { color: #2a6f97; font-size: 12pt; margin-top: 12pt; }
      table { border-collapse: collapse; width: 100%; margin: 6pt 0 12pt; }
      th, td { border: 1px solid #b9c2cc; padding: 4pt 8pt; font-size: 10pt; text-align: left; }
      th { background: #eef3f7; }
      .meta { color: #5a6472; font-size: 10pt; }
    </style></head><body>
      <h1>${esc(t("reports.word.title"))}</h1>
      <p class="meta">${esc(t("reports.word.generated", { date: generated }))} &#183; ${esc(t("reports.cohortCount", { n: agg.totalWhanau }))}${agg.filtered ? ` &#183; ${esc(t("reports.word.filtered"))}` : ""}</p>
      <h2>${esc(t("reports.whanauState.nowTitle"))}</h2>
      <p class="meta">${esc(t("reports.whanauState.entryExitLead"))}</p>
      ${wsTable}
      ${trTable}
      ${popTable}
      ${pouTable}
      <h2>${esc(t("reports.toc.title"))}</h2>
      ${tocSection}
    </body></html>`;
  };

  const download = () => {
    const blob = new Blob(["﻿", buildHtml()], {
      type: "application/msword",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `whanau-factor-report-${new Date().toISOString().slice(0, 10)}.doc`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <button
      type="button"
      className="rpt-word-btn"
      onClick={download}
      style={{
        minHeight: "36px",
        padding: "4px 14px",
        borderRadius: "8px",
        border: "1px solid var(--teal, #2a9d8f)",
        background: "var(--teal, #2a9d8f)",
        color: "#fff",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {t("reports.word.download")}
    </button>
  );
}

export default WordExportButton;
